import { FC, useState } from 'react';
import useHome from '@/hooks/use-home.hook';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { Box, ClickAwayListener } from '@mui/material';
import TileContent from '@/components/tile-group/content';
import EmptyTile from '@/components/tile-group/empty-tile';
import TileGroup from '@/components/tile-group';
import TileGroupHeader from '@/components/tile-group/header';
import { TILE_COMPONENTS } from '@/components/tile-group/tile/helpers';

const FavoritesTileGroup: FC = () => {
  const { favorites } = useHome();
  const { moveFavorite } = useUserSettings();
  const [sortEnabled, setSortEnabled] = useState(false);

  const showEmptyTile = !favorites.length;

  // configure sensors for mouse, touch, and keyboard
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      moveFavorite(String(active.id), String(over.id));
    }
  };

  const handleToggleSort = () => {
    setSortEnabled(!sortEnabled);
  };

  const handleClickAway = () => {
    if (sortEnabled) {
      setSortEnabled(false);
    }
  };

  const canSort = favorites.length > 1;
  const sortDisabled = !sortEnabled || !canSort;

  let SortEl = (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext items={favorites} strategy={rectSortingStrategy}>
          <TileContent>
            {favorites.map((tile) => {
              const TileEl = TILE_COMPONENTS[tile.tileType];

              if (TileEl) {
                return <TileEl tile={tile} sortable={!sortDisabled} key={tile.tileId} />;
              }
            })}
            {showEmptyTile && <EmptyTile />}
          </TileContent>
        </SortableContext>
      </DndContext>
    </Box>
  );

  if (sortEnabled) {
    SortEl = <ClickAwayListener onClickAway={handleClickAway}>{SortEl}</ClickAwayListener>;
  }

  return (
    <TileGroup>
      <TileGroupHeader
        title="Favorites"
        icon="favorite"
        onSort={canSort ? handleToggleSort : undefined}
        sorting={sortEnabled}
      />
      {SortEl}
    </TileGroup>
  );
};

export default FavoritesTileGroup;
